<template>
  <div
    v-if="show"
    class="modal fade show d-block"
    style="background: rgba(0, 0, 0, 0.5)"
    @click.self="$emit('close')"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ isEditing ? "編輯變體" : "新增變體" }}
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')">
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveVariant">
            <!-- Dynamic Options -->
            <div class="row mb-3" v-if="productOptions.length > 0">
              <h6 class="text-sm text-uppercase text-secondary mb-3">
                屬性規格
              </h6>
              <div
                class="col-md-4 mb-3"
                v-for="(opt, index) in productOptions"
                :key="index"
              >
                <label class="form-control-label">{{ opt.name }}</label>

                <!-- Select if values exist -->
                <select
                  v-if="opt.values && opt.values.length > 0"
                  class="form-control"
                  v-model="variant.option_values[opt.name]"
                >
                  <option value="">選擇{{ opt.name }}</option>
                  <option v-for="val in opt.values" :key="val" :value="val">
                    {{ val }}
                  </option>
                </select>

                <!-- Text input if no values defined -->
                <soft-input
                  v-else
                  type="text"
                  :placeholder="'輸入' + opt.name"
                  v-model="variant.option_values[opt.name]"
                />
              </div>
            </div>

            <!-- Variant Code (SKU) -->
            <div class="mb-3">
              <label class="form-control-label">變體編號 (SKU)</label>
              <soft-input
                type="text"
                placeholder="例如：DRIVER-001-SR"
                v-model="variant.variant_code"
                required
              />
            </div>

            <!-- Variant Name -->
            <div class="mb-3">
              <label class="form-control-label"
                >變體名稱（選填，留空自動生成）</label
              >
              <soft-input
                type="text"
                placeholder="例如：Stiff / 10.5° / 標準長"
                v-model="variant.variant_name"
              />
            </div>

            <hr class="horizontal dark" />

            <!-- Price & Stock -->
            <h6 class="text-sm text-uppercase text-secondary mb-3">
              價格與庫存
            </h6>
            <div class="row">
              <div class="col-md-4">
                <label class="form-control-label">價格 *</label>
                <soft-input
                  type="number"
                  placeholder="價格"
                  v-model="variant.price"
                  step="0.01"
                  required
                  @input="calculateVariantSalePrice"
                />
              </div>
              <div class="col-md-4">
                <label
                  class="form-control-label text-xs d-flex justify-content-between"
                >
                  特價 (Sale Price)
                  <span
                    v-if="variantDiscountPercentage > 0"
                    class="badge bg-danger text-white"
                    >-{{ variantDiscountPercentage }}%</span
                  >
                </label>
                <soft-input
                  type="number"
                  placeholder="特價 (留空代表無折扣)"
                  v-model="variant.sale_price"
                  step="0.01"
                  @input="calculateDiscountFromSalePrice"
                />
                <small
                  v-if="variant.sale_price && variant.price"
                  class="text-xs text-muted"
                >
                  節省: {{ Math.max(0, variant.price - variant.sale_price) }}
                </small>
              </div>
              <div class="col-md-4">
                <label class="form-control-label">庫存 *</label>
                <soft-input
                  type="number"
                  placeholder="庫存"
                  v-model="variant.stock"
                  required
                />
              </div>
            </div>

            <div class="mt-3">
              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="isCustomized"
                  v-model="variant.is_customized"
                />
                <label class="form-check-label" for="isCustomized"
                  >客製化商品</label
                >
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <soft-button
            color="secondary"
            variant="outline"
            @click="$emit('close')"
          >
            取消
          </soft-button>
          <soft-button
            color="success"
            variant="outline"
            @click="saveAndAdd"
            class="me-2"
          >
            儲存並新增下一筆
          </soft-button>
          <soft-button color="success" variant="gradient" @click="saveVariant">
            儲存
          </soft-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SoftInput from "@/components/SoftInput.vue";
import SoftButton from "@/components/SoftButton.vue";

export default {
  name: "ProductVariantModal",
  components: {
    SoftInput,
    SoftButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    variantData: {
      type: Object,
      default: null,
    },
    productType: {
      type: String,
      default: "",
    },
    productOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      variant: {
        variant_code: "",
        variant_name: "",
        price: 0,
        suggested_price: null,
        sale_price: null,
        stock: 1,
        shaft_flex: "",
        loft_angle: null,
        hand: "",
        length: "",
        length_adjustment: null,
        weight: null,
        shaft_brand: "",
        shaft_weight: "",
        skill_level: "",
        gender: "",
        weight_type: "single",
        is_customized: false,
        option_values: {},
      },
      isEditing: false,
    };
  },
  computed: {
    variantDiscountPercentage() {
      if (!this.variant.price || !this.variant.sale_price) return 0;
      if (Number(this.variant.sale_price) >= Number(this.variant.price))
        return 0;
      return Math.round(
        ((this.variant.price - this.variant.sale_price) / this.variant.price) *
          100
      );
    },
    isClub() {
      if (!this.productType) return false;
      const clubTypes = [
        "driver",
        "1_wood",
        "mini_driver",
        "fairway_wood",
        "3_wood",
        "4_wood",
        "5_wood",
        "7_wood",
        "fairway_other",
        "iron_set",
        "single_iron",
        "hybrid",
        "2_hybrid",
        "3_hybrid",
        "4_hybrid",
        "5_hybrid",
        "hybrid_other",
        "wedge",
        "putter",
        "putter_blade",
        "putter_mallet",
        "putter_arm_lock",
        "putter_other",
      ];
      return clubTypes.includes(this.productType);
    },
    isClothing() {
      if (!this.productType) return false;
      return ["apparel", "shoes", "accessory"].includes(this.productType);
    },
  },
  watch: {
    variantData: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.variant = JSON.parse(JSON.stringify(newVal)); // Deep copy to detach
          this.isEditing = true;

          // Fix: Ensure option_values is an object, not an array
          if (
            !this.variant.option_values ||
            Array.isArray(this.variant.option_values)
          ) {
            this.variant.option_values = {};
          }

          // Reverse Hydration: Try to populate option_values from flat fields (size, color...) if missing
          // This fixes display for variants that lost their option_values json but have the data in columns
          this.productOptions.forEach((opt) => {
            const key = opt.name;
            if (!this.variant.option_values[key]) {
              // Try to find matching backend field
              // Reverse of mapOptionsToFields logic (simplified)
              const lowerKey = key.toLowerCase();
              if (lowerKey === "size" && this.variant.size) {
                this.variant.option_values[key] = this.variant.size;
              } else if (lowerKey === "color" && this.variant.color) {
                this.variant.option_values[key] = this.variant.color;
              } else if (lowerKey === "hand" && this.variant.hand) {
                // Hand is usually lowercase in DB ("right"), but option might be "Right"
                // Just take the value as is
                this.variant.option_values[key] = this.variant.hand;
              } else if (
                (lowerKey === "loft" || lowerKey === "loft angle") &&
                this.variant.loft_angle
              ) {
                this.variant.option_values[key] = this.variant.loft_angle;
              } else if (lowerKey === "shaft" && this.variant.shaft_brand) {
                this.variant.option_values[key] = this.variant.shaft_brand;
              } else if (lowerKey === "flex" && this.variant.shaft_flex) {
                this.variant.option_values[key] = this.variant.shaft_flex;
              }
            }
          });
        } else {
          this.resetForm();
        }
      },
    },
  },
  methods: {
    calculateDiscountFromSalePrice() {
      // Optional validation logic
      if (this.variant.sale_price && Number(this.variant.sale_price) < 0) {
        this.variant.sale_price = 0;
      }
    },
    hasAttribute(attr) {
      return this.activeAttributes && this.activeAttributes.includes(attr);
    },
    saveVariant() {
      // Ensure numeric
      this.variant.price = parseFloat(this.variant.price);
      if (this.variant.sale_price)
        this.variant.sale_price = parseFloat(this.variant.sale_price);

      this.mapOptionsToFields();
      const variantData = JSON.parse(JSON.stringify(this.variant));
      this.$emit("save", { ...variantData, close: true });
    },
    saveAndAdd() {
      if (!this.variant.variant_code) {
        alert("請填寫 SKU");
        return;
      }
      this.variant.price = parseFloat(this.variant.price);
      if (this.variant.sale_price)
        this.variant.sale_price = parseFloat(this.variant.sale_price);

      this.mapOptionsToFields();
      // Deep clone to prevent reference issues with option_values
      const variantData = JSON.parse(JSON.stringify(this.variant));
      this.$emit("save", { ...variantData, close: false });

      // Reset SKU and Name for next entry, keep others
      this.variant.variant_code = "";
      this.variant.variant_name = "";
    },
    mapOptionsToFields() {
      // Map dynamic option_values to specific backend fields if they exist
      const mapping = {
        Hand: "hand",
        Flex: "shaft_flex",
        Loft: "loft_angle",
        Length: "length",
        Shaft: "shaft_brand", // or shaft_model depending on backend
        "Shaft Weight": "shaft_weight",
        Size: "size",
        Color: "color",
        Material: "material",
        Weight: "weight",
      };

      for (const [key, val] of Object.entries(this.variant.option_values)) {
        const backendField =
          mapping[key] || mapping[key.charAt(0).toUpperCase() + key.slice(1)]; // Handle case sensitivity
        if (backendField) {
          // Special handling for hand
          if (backendField === "hand") {
            this.variant.hand = val.toLowerCase();
          } else {
            this.variant[backendField] = val;
          }
        }
      }
    },
    resetForm() {
      this.variant = {
        variant_code: "",
        variant_name: "",
        price: 0,
        suggested_price: null,
        sale_price: null,
        stock: 1,
        shaft_flex: "",
        loft_angle: null,
        hand: "",
        length: "",
        length_adjustment: null,
        weight: null,
        shaft_brand: "",
        shaft_weight: "",
        skill_level: "",
        gender: "",
        weight_type: "single",
        is_customized: false,
        option_values: {},
      };
      this.isEditing = false;
    },
  },
};
</script>
